/**
 * `<NoteStatsStrip>` — Phase 4 (LD-7).
 *
 * Six-card stat strip displayed above the Table of Contents on the Progress
 * Note (both edit + viewer). Mirrors the mockup at
 * `ChartPage.tsx:4000-4079`. Cards:
 *
 *   | Card              | Color    | Icon                     | Value                                       |
 *   |-------------------|----------|--------------------------|---------------------------------------------|
 *   | Session Duration  | coral    | IconVideo                | `Encounter.length` (or period.end-start) in minutes |
 *   | Note Completed    | navy     | IconClock                | `DocumentReference.date` relative ("30m ago") |
 *   | Lock State        | dynamic  | IconLock / IconHourglass | "Locked In Xh Ym" / "Xh left to Lock" / "OVERDUE" |
 *   | Diagnoses         | red      | IconClipboard            | encounter-diagnosis Condition count          |
 *   | Orders            | amber    | IconFileText             | ServiceRequest count linked to the Encounter |
 *   | Medications       | blue     | IconPill                 | MedicationStatement count linked to the Encounter |
 *
 * Counts: live FHIR search by Encounter (uses `_summary=count`). When the
 * encounter isn't available (note still draft) OR the count query is loading,
 * the `<NoteStatsStripFormBridge>` variant reads the RHF structured arrays
 * so the strip stays meaningful during the edit-mid-flow scenario.
 *
 * Lock state machine (LD-7):
 *   - `docStatus === 'final'`            → green  "Locked In Xh Ym"
 *   - elapsed within LOCK_WINDOW_MS      → orange "Xh left to Lock"
 *   - else (preliminary AND past 24h)    → red    "OVERDUE" (does NOT block locking)
 *
 * The Lock State card owns its own `setInterval(60_000)` ticker so only that
 * card re-renders every minute. The other cards never tick.
 */
import { useEffect, useMemo, useState } from 'react'
import { Card, Group, SimpleGrid, Text } from '@mantine/core'
import {
  IconClipboard,
  IconClock,
  IconFileText,
  IconHourglass,
  IconLock,
  IconPill,
  IconVideo,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { useQuery } from '@tanstack/react-query'
import { useFormContext, useWatch } from 'react-hook-form'
import type { DocumentReference, Encounter, Provenance, ResourceType } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import type { ProgressNoteEditFormValues } from '../ProgressNoteEdit/progressNoteEditSchema'
import { MONO_FONT, PROGRESS_NOTE_CORAL as COLOR_CORAL } from '../ProgressNoteEdit/tokens'

/** Hardcoded 24h window per LD-7. */
const LOCK_WINDOW_MS = 24 * 60 * 60 * 1000

const COLOR_NAVY = '#1e3a5f'
const COLOR_GREEN = '#16a34a'
const COLOR_AMBER = '#d97706'
const COLOR_RED = '#dc2626'
const COLOR_BLUE = '#2563eb'

export interface NoteStatsStripProps {
  encounter?: Encounter
  documentReference: DocumentReference
  provenance?: Provenance
  /**
   * Optional structured-array fallback for when the encounter doesn't yet
   * carry the resources (note still in draft inside the editor). Wire this
   * from `<ProgressNoteEdit>` via `useWatch`. The viewer can omit it.
   */
  formFallback?: {
    diagnoses: number
    orders: number
    medications: number
  }
  /**
   * Gate-clinical-writes-until-Lock: when `true`, the note is PRELIMINARY and
   * its clinical resources have not been materialized yet (that happens at
   * Lock). The live encounter/patient `_summary=count` queries would all read
   * 0, so we skip them entirely and read the proposed counts straight from
   * `formFallback` (sourced from the draft QR or the live RHF form state).
   * After Lock (`final`) this is `false` and the strip counts the real chart
   * resources as before.
   */
  draftCounts?: boolean
}

// ---------------------------------------------------------------------------
// Time helpers (no relativeTime dayjs plugin — keep the strip self-contained)
// ---------------------------------------------------------------------------

function formatDurationCompact(minutes: number | null | undefined): string {
  if (minutes == null) return '—'
  if (minutes <= 0) return '< 1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`
}

/** Lightweight `fromNow`-style — avoids extending dayjs globally. */
function formatRelative(iso: string | undefined): string {
  if (!iso) return '—'
  const then = dayjs(iso)
  if (!then.isValid()) return '—'
  const now = dayjs()
  const diffSec = now.diff(then, 'second')
  if (diffSec < 60) return 'Just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)}h ago`
  if (now.startOf('day').diff(then.startOf('day'), 'day') === 1) return 'Yesterday'
  const days = Math.floor(diffSec / 86_400)
  if (days < 30) return `${days}d ago`
  return then.format('MMM D, YYYY')
}

function getSessionDurationMinutes(encounter?: Encounter): number | null {
  if (!encounter) return null
  if (typeof encounter.length?.value === 'number') {
    const unit = encounter.length.unit?.toLowerCase() || encounter.length.code?.toLowerCase() || ''
    // Most encounters store length in minutes; defensively handle hours.
    if (unit.includes('h') || unit === 'hour' || unit === 'hours') {
      return Math.round(encounter.length.value * 60)
    }
    return Math.round(encounter.length.value)
  }
  const start = encounter.period?.start
  const end = encounter.period?.end
  if (!start || !end) return null
  const startMs = dayjs(start).valueOf()
  const endMs = dayjs(end).valueOf()
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null
  if (endMs <= startMs) return null
  return Math.round((endMs - startMs) / 60_000)
}

// ---------------------------------------------------------------------------
// Encounter-scoped FHIR count helper
// ---------------------------------------------------------------------------

/**
 * Live count of FHIR resources that link to a given Encounter. Uses Medplum's
 * `_summary=count` to skip resource hydration — returns just `bundle.total`.
 * Returns `null` when:
 *   - the encounter id is undefined (note still draft, no Encounter yet), OR
 *   - the count query is loading / errored (caller falls back to the form's
 *     local array length).
 */
function useEncounterResourceCount(
  resourceType: ResourceType,
  encounterId: string | undefined,
  /** Optional second link key when the resource uses something other than
   *  `encounter` (e.g. MedicationStatement uses `context`). */
  linkKey: string = 'encounter',
  extraParams = '',
  /** When `false`, the live count query is disabled (preliminary note — the
   *  resources don't exist yet; the caller reads the draft fallback). */
  enabled = true,
): number | null {
  const medplum = useMedplum()
  const query = useQuery({
    enabled: enabled && !!encounterId,
    queryKey: ['note-stats-count', resourceType, encounterId, linkKey, extraParams],
    staleTime: 30_000,
    queryFn: async () => {
      const bundle = await medplum.search(
        resourceType,
        `${linkKey}=Encounter/${encounterId}${extraParams ? `&${extraParams}` : ''}&_summary=count`,
      )
      return bundle.total ?? 0
    },
  })
  return query.data ?? null
}

/**
 * Patient-scoped count for resources whose encounter-linkage is unreliable.
 *
 * MedicationStatement specifically can't be counted by `context=Encounter/X`:
 * the cascade dedups by RxNorm code via FHIR `If-None-Exist`, so a re-prescribe
 * of an already-active med returns the EXISTING MedicationStatement (whose
 * `context` still points at a prior encounter) without updating context. That
 * makes the encounter-scoped count always 0 for refills. Counting active meds
 * on the patient sidesteps the dedup quirk and matches the mockup's intent
 * ("current med list", not "meds touched this visit").
 */
function usePatientResourceCount(
  resourceType: ResourceType,
  patientId: string | undefined,
  patientLinkKey: 'patient' | 'subject' = 'subject',
  extraParams = '',
  /** When `false`, the live count query is disabled (preliminary note). */
  enabled = true,
): number | null {
  const medplum = useMedplum()
  const query = useQuery({
    enabled: enabled && !!patientId,
    queryKey: ['note-stats-count-patient', resourceType, patientId, patientLinkKey, extraParams],
    staleTime: 30_000,
    queryFn: async () => {
      const bundle = await medplum.search(
        resourceType,
        `${patientLinkKey}=Patient/${patientId}${extraParams ? `&${extraParams}` : ''}&_summary=count`,
      )
      return bundle.total ?? 0
    },
  })
  return query.data ?? null
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface StatCardProps {
  icon: React.ReactNode
  value: string
  label: string
  color: string
  borderColor: string
}

function StatCard({ icon, value, label, color, borderColor }: StatCardProps) {
  return (
    <Card
      withBorder
      radius={10}
      padding="sm"
      className="flex min-w-[110px] flex-col gap-1 border-t-[3px]"
      style={{ borderTopColor: borderColor }}
    >
      <Group gap={6} align="center">
        {icon}
      </Group>
      <Text
        fw={700}
        style={{ fontFamily: MONO_FONT, color, lineHeight: 1.1, marginTop: 2, fontSize: 20 }}
      >
        {value}
      </Text>
      <Text
        size="xs"
        fw={700}
        c="dimmed"
        tt="uppercase"
        style={{ fontSize: 10, letterSpacing: '0.07em' }}
      >
        {label}
      </Text>
    </Card>
  )
}

/**
 * Self-ticking Lock card. Re-evaluates the state every 60s using `now()`.
 * Keeps the rest of the strip stable across ticks.
 */
function LockStateCard({
  documentReference,
  provenance,
}: {
  documentReference: DocumentReference
  provenance?: Provenance
}) {
  const [, setNowTick] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const docStatus = documentReference.docStatus
  const addedIso = documentReference.date
  const lastUpdated = documentReference.meta?.lastUpdated
  const provenanceRecorded = provenance?.recorded

  if (docStatus === 'final') {
    // "Locked In Xh Ym" — span between Composition.date (≈ DocumentReference.date)
    // and Provenance.recorded (fallback to DocumentReference.meta.lastUpdated).
    const lockedAt = provenanceRecorded || lastUpdated
    const lockMinutes =
      addedIso && lockedAt
        ? Math.max(0, Math.round((dayjs(lockedAt).valueOf() - dayjs(addedIso).valueOf()) / 60_000))
        : null
    return (
      <StatCard
        icon={<IconLock size={18} color={COLOR_GREEN} />}
        value={formatDurationCompact(lockMinutes)}
        label="Locked In"
        color={COLOR_GREEN}
        borderColor={COLOR_GREEN}
      />
    )
  }

  // Preliminary path — countdown / overdue.
  const addedMs = addedIso ? dayjs(addedIso).valueOf() : null
  const deadlineMs = addedMs != null ? addedMs + LOCK_WINDOW_MS : null
  const minsRemaining = deadlineMs != null ? Math.round((deadlineMs - Date.now()) / 60_000) : null

  if (minsRemaining != null && minsRemaining > 0) {
    return (
      <StatCard
        icon={<IconHourglass size={18} color={COLOR_AMBER} />}
        value={formatDurationCompact(minsRemaining)}
        label="Hours Left to Lock"
        color={COLOR_AMBER}
        borderColor={COLOR_AMBER}
      />
    )
  }

  return (
    <StatCard
      icon={<IconHourglass size={18} color={COLOR_RED} />}
      value="OVERDUE"
      label="Past 24h Limit"
      color={COLOR_RED}
      borderColor={COLOR_RED}
    />
  )
}

// ---------------------------------------------------------------------------
// Main strip (form-agnostic) — pass `formFallback` if the caller is inside a
// FormProvider (the edit shell wraps it with `<NoteStatsStripFormBridge>`).
// ---------------------------------------------------------------------------

export function NoteStatsStrip({
  encounter,
  documentReference,
  provenance,
  formFallback,
  draftCounts,
}: NoteStatsStripProps) {
  const encounterId = encounter?.id
  // Patient id for the medications count (see comment on `medsCount` below).
  const patientId = documentReference.subject?.reference?.split('/')[1]

  // Gate-clinical-writes-until-Lock: for a PRELIMINARY note the clinical
  // resources aren't materialized yet, so the live counts would all read 0.
  // Disable them and read the proposed counts from `formFallback` (the draft
  // QR / live RHF form state) instead. After Lock the strip counts the real
  // resources.
  const liveCountsEnabled = !draftCounts

  // Live counts (null while loading / disabled / when no encounter id — we
  // fall back to `formFallback`).
  const dxCount = useEncounterResourceCount(
    'Condition',
    encounterId,
    'encounter',
    'category=encounter-diagnosis',
    liveCountsEnabled,
  )
  const ordersCount = useEncounterResourceCount(
    'ServiceRequest',
    encounterId,
    'encounter',
    '',
    liveCountsEnabled,
  )
  // Medications: count active patient-wide, not encounter-scoped. The cascade
  // dedups MedicationStatement by RxNorm via `If-None-Exist`, so a refill
  // returns the existing row whose `context` still points at the prior
  // encounter — making the encounter-scoped count always 0 for refills. The
  // mockup card means "current med list" anyway.
  const medsCount = usePatientResourceCount(
    'MedicationStatement',
    patientId,
    'subject',
    'status=active',
    liveCountsEnabled,
  )

  const diagnoses = dxCount ?? formFallback?.diagnoses ?? 0
  const orders = ordersCount ?? formFallback?.orders ?? 0
  const medications = medsCount ?? formFallback?.medications ?? 0

  const sessionMinutes = getSessionDurationMinutes(encounter)
  const isLocked =
    documentReference.docStatus === 'final' || documentReference.docStatus === 'amended'
  const completionLabel = isLocked ? 'Completed' : 'Started'
  const completionAnchorIso = isLocked
    ? provenance?.recorded || documentReference.meta?.lastUpdated
    : documentReference.date
  const completionText = formatRelative(completionAnchorIso)

  return (
    <SimpleGrid
      cols={{ base: 2, sm: 3, md: 6 }}
      spacing="sm"
      mb="lg"
      data-testid="note-stats-strip"
    >
      <StatCard
        icon={<IconVideo size={18} color={COLOR_CORAL} />}
        value={formatDurationCompact(sessionMinutes)}
        label="Session Duration"
        color={COLOR_CORAL}
        borderColor={COLOR_CORAL}
      />
      <StatCard
        icon={<IconClock size={18} color={COLOR_NAVY} />}
        value={completionText}
        label={completionLabel}
        color={COLOR_NAVY}
        borderColor={COLOR_NAVY}
      />
      <LockStateCard documentReference={documentReference} provenance={provenance} />
      <StatCard
        icon={<IconClipboard size={18} color={COLOR_RED} />}
        value={String(diagnoses)}
        label="Diagnoses"
        color={COLOR_RED}
        borderColor={COLOR_RED}
      />
      <StatCard
        icon={<IconFileText size={18} color={COLOR_AMBER} />}
        value={String(orders)}
        label="Orders"
        color={COLOR_AMBER}
        borderColor={COLOR_AMBER}
      />
      <StatCard
        icon={<IconPill size={18} color={COLOR_BLUE} />}
        value={String(medications)}
        label="Medications"
        color={COLOR_BLUE}
        borderColor={COLOR_BLUE}
      />
    </SimpleGrid>
  )
}

// ---------------------------------------------------------------------------
// FormProvider-aware wrapper — used inside `<ProgressNoteEdit>` so the strip
// can fall back to the editor's RHF state when the FHIR encounter still has
// nothing to count against. Calling code that does NOT live inside a
// FormProvider should use `<NoteStatsStrip>` directly.
// ---------------------------------------------------------------------------

export function NoteStatsStripFormBridge({
  encounter,
  documentReference,
  provenance,
}: Omit<NoteStatsStripProps, 'formFallback'>) {
  const { control } = useFormContext<ProgressNoteEditFormValues>()
  const conditions = useWatch({ control, name: 'conditions' })
  const serviceRequests = useWatch({ control, name: 'serviceRequests' })
  const medications = useWatch({ control, name: 'medications' })

  const formFallback = useMemo(
    () => ({
      diagnoses: Array.isArray(conditions)
        ? conditions.filter((c) => c?.category === 'encounter-diagnosis').length
        : 0,
      orders: Array.isArray(serviceRequests) ? serviceRequests.length : 0,
      medications: Array.isArray(medications) ? medications.length : 0,
    }),
    [conditions, serviceRequests, medications],
  )

  // While editing inside `<ProgressNoteEdit>`, the note is PRELIMINARY (or a
  // brand-new create-mode draft) unless it's already locked. Drive the counts
  // off the live RHF form state (`formFallback`) rather than the not-yet-
  // materialized FHIR resources. A `final` doc here would be read-only anyway.
  const draftCounts = documentReference.docStatus !== 'final'

  return (
    <NoteStatsStrip
      encounter={encounter}
      documentReference={documentReference}
      provenance={provenance}
      formFallback={formFallback}
      draftCounts={draftCounts}
    />
  )
}
